import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  BookOpen, Calendar, FileText, TrendingUp, Briefcase, Zap,
  ArrowRight, Download, Users, Star, Clock, Linkedin,
  Instagram, Youtube, Play, ExternalLink
} from 'lucide-react';

const Blog = () => {
  const navigate = useNavigate();

  const articles = [
    {
      id: 1,
      title: 'Kto si investor? 4 kategórie podľa reality (Nie podľa ega)',
      description: 'Zisti, do ktorej zo 4 kategórií investorov skutočne patríš podľa rezervy a NAV — nie podľa pocitov. Prehľadná tabuľka a ďalší krok pre tvoju fázu.',
      category: 'Systém Renty',
      image: '/pribeh_1z4.jpeg',
      date: '25. Mar 2026',
      readTime: '14 min',
      link: '/blog/kto-si-investor-4-kategorie-podla-reality'
    }
  ];

  const mediaOutlets = [{ name: 'Forbes', link: '#', logoText: 'FORBES' }];

  return (
    <div className="blog-page font-body text-brand-dark-blue overflow-x-hidden">

      {/* 1. HERO SECTION - WITH PHOTO & SOCIALS */}
      <section className="relative bg-brand-dark-blue text-white pt-20 lg:pt-32 pb-24 overflow-x-clip overflow-y-visible">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url(/symbol-3.svg)', backgroundSize: '400px', backgroundRepeat: 'repeat' }} />

        <div className="container mx-auto px-4 sm:px-5 relative z-10 max-w-full">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center max-w-7xl mx-auto">

            {/* Left: Text Content */}
            <div className="lg:col-span-7 space-y-6 sm:space-y-8 animate-fade-in min-w-0">
              <div className="inline-flex items-center gap-2 px-3 py-2 sm:px-4 rounded-full bg-brand-orange/10 border border-brand-orange/20 text-brand-orange text-xs sm:text-sm font-bold uppercase tracking-widest backdrop-blur-sm max-w-full">
                <Zap size={14} className="fill-brand-orange shrink-0" />
                <span className="leading-tight">Centrum informácií</span>
              </div>

              <h1 className="text-[1.75rem] min-[400px]:text-[2rem] sm:text-4xl md:text-5xl lg:text-7xl font-heading font-bold leading-[1.12] sm:leading-tight break-words">
                Know-how pre{' '}
                <span className="text-brand-orange block sm:inline">normálne bohatých</span>
              </h1>

              <p className="text-base sm:text-xl lg:text-2xl text-gray-300 leading-relaxed font-light max-w-2xl">
                Miesto, kde nájdete články, týždenné video prehľady (90-ka) a vedomosti o majetku a rente, ktoré v bežných médiách nenájdete.
              </p>

              <div className="pt-2 sm:pt-4 space-y-3 sm:space-y-4">
                <p className="text-xs sm:text-sm font-bold uppercase tracking-widest text-gray-400">Sledujte ma denne na:</p>
                <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 w-full max-w-xl">
                  <a href="https://www.linkedin.com/in/petosloboda/" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center sm:justify-start gap-3 bg-white/5 hover:bg-white/10 border border-white/10 px-4 py-3 sm:px-5 rounded-xl transition-all group min-h-[48px] w-full sm:flex-1 sm:min-w-[140px]">
                    <Linkedin className="text-brand-orange group-hover:scale-110 transition-transform shrink-0" />
                    <span className="font-bold">LinkedIn</span>
                  </a>
                  <a href="https://www.instagram.com/petosloboda/" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center sm:justify-start gap-3 bg-white/5 hover:bg-white/10 border border-white/10 px-4 py-3 sm:px-5 rounded-xl transition-all group min-h-[48px] w-full sm:flex-1 sm:min-w-[140px]">
                    <Instagram className="text-brand-orange group-hover:scale-110 transition-transform shrink-0" />
                    <span className="font-bold">Instagram</span>
                  </a>
                  <a href="https://www.youtube.com/@peto.sloboda" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center sm:justify-start gap-3 bg-white/5 hover:bg-white/10 border border-white/10 px-4 py-3 sm:px-5 rounded-xl transition-all group min-h-[48px] w-full sm:flex-1 sm:min-w-[140px]">
                    <Youtube className="text-brand-orange group-hover:scale-110 transition-transform shrink-0" />
                    <span className="font-bold">YouTube</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Right: Peťo Photo */}
            <div className="lg:col-span-5 relative hidden lg:block animate-fade-in-up">
              <div className="absolute -inset-4 bg-brand-orange/10 rounded-[3rem] transform rotate-3" />
              <div className="relative rounded-[2.5rem] overflow-hidden shadow-2xl skew-y-1">
                <img src="/kariera_hero.jpeg" className="w-full h-full object-cover aspect-[4/5]" alt="Peťo Sloboda" />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-dark-blue/60 to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. MEDIA & INTERVIEWS SECTION */}
      <section className="py-10 sm:py-12 bg-white border-b border-gray-100">
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 sm:gap-8 lg:gap-16 text-center sm:text-left">
            <div className="shrink-0">
              <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-gray-400">Napísali o mne:</h2>
            </div>
            <div className="flex flex-wrap justify-center items-center opacity-60 grayscale hover:grayscale-0 hover:opacity-100 transition-all duration-500">
              {mediaOutlets.map((media, i) => (
                <a key={i} href={media.link} className="font-heading font-black text-3xl sm:text-4xl lg:text-3xl text-brand-dark-blue tracking-tighter hover:text-brand-orange transition-colors px-2">
                  {media.logoText}
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 3. 90-KA VIDEO FORMAT HIGHLIGHT */}
      <section className="py-16 sm:py-24 bg-gray-50 overflow-x-clip">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">

            {/* Video Mockup / Preview */}
            <div className="relative flex justify-center order-2 lg:order-1 w-full min-w-0">
              <div className="w-[min(100%,18rem)] sm:w-72 lg:w-80 max-w-full h-[min(70vh,580px)] sm:h-[580px] bg-gray-900 rounded-[2rem] sm:rounded-[3rem] p-2 sm:p-3 shadow-2xl border-4 border-gray-800 relative group overflow-hidden mx-auto">
                <img src="/pribeh_s3.jpeg" className="w-full h-full object-cover rounded-[1.65rem] sm:rounded-[2.5rem] opacity-80 group-hover:scale-105 transition-transform duration-700" alt="Video Preview" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-20 bg-brand-orange/90 text-white rounded-full flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform cursor-pointer">
                    <Play size={40} className="ml-1" />
                  </div>
                </div>
                <div className="absolute bottom-10 left-8 right-8 text-white">
                  <div className="text-xs font-bold uppercase tracking-widest mb-2 flex items-center gap-2">
                    <Zap size={12} className="fill-brand-orange text-brand-orange" />
                    90-KA #142
                  </div>
                  <p className="text-lg font-bold leading-tight">Prečo nenechávam peniaze len tak na účte?</p>
                </div>
              </div>

            </div>

            {/* Content */}
            <div className="space-y-8 order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 text-brand-orange font-bold uppercase tracking-widest text-xs">
                <div className="w-12 h-0.5 bg-brand-orange" />
                Video formát
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-6xl font-heading font-bold leading-tight break-words">
                Čo je to <br /><span className="text-brand-orange">90-KA?</span>
              </h2>
              <p className="text-base sm:text-xl text-gray-600 leading-relaxed font-light">
                Môj pravidelný týždenný video formát. To najdôležitejšie zo sveta peňazí a biznisu zhrnuté v 90 sekundách. Žiadne omáčky, len čistá podstata a prax z reálneho sveta.
              </p>

              <div className="space-y-4 pt-4">
                {[
                  'Zrozumiteľne pre každého',
                  'Bez produktových letákov',
                  'Priamo z praxe architekta majetku',
                  'Systémové videnie sveta'
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 font-bold text-gray-700">
                    <div className="w-5 h-5 bg-green-100 text-green-600 rounded-full flex items-center justify-center"><ArrowRight size={12} /></div>
                    {item}
                  </div>
                ))}
              </div>

              <div className="pt-6">
                <a href="https://www.youtube.com/@peto.sloboda" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 bg-brand-dark-blue text-white font-bold py-5 px-10 rounded-2xl hover:bg-brand-orange transition-all shadow-xl">
                  Sledovať všetky diely <ArrowRight size={20} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. ARTICLES GRID */}
      <section id="clanky" className="py-24 bg-white">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="flex flex-col md:flex-row justify-between items-stretch md:items-end mb-12 sm:mb-16 gap-6 md:gap-8">
            <div className="min-w-0">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold mb-3 sm:mb-4 leading-tight break-words">Miera vedomostí rastie</h2>
              <p className="text-base sm:text-lg text-gray-500">Vyberte si tému, ktorá vás momentálne zaujíma najviac.</p>
            </div>
            <div className="flex flex-wrap gap-2 w-full md:w-auto md:max-w-none justify-start md:justify-end">
              {['Všetko', 'Systém', 'Majetok', 'Médiá'].map((cat, i) => (
                <button key={i} type="button" className={`px-4 py-2.5 sm:px-5 rounded-xl font-bold text-xs sm:text-sm transition-all min-h-[44px] ${i === 0 ? 'bg-brand-orange text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                  {cat}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {articles.map((article) => (
              <Link
                key={article.id}
                to={article.isExternal ? '#' : article.link}
                className="group flex flex-col bg-white rounded-[2rem] overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-gray-100"
              >
                <div className="relative h-64 overflow-hidden">
                  <img src={article.image} alt={article.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  {article.isExternal && (
                    <div className="absolute top-4 right-4 animate-pulse">
                      <span className="px-3 py-1 bg-white/90 backdrop-blur-md rounded-lg text-[10px] font-black text-brand-orange uppercase tracking-widest flex items-center gap-1.5">
                        <ExternalLink size={12} /> Rozhovor
                      </span>
                    </div>
                  )}
                  <div className="absolute top-4 left-4">
                    <span className="px-4 py-1.5 bg-white/90 backdrop-blur-md rounded-lg text-xs font-bold text-brand-dark-blue uppercase tracking-widest">
                      {article.category}
                    </span>
                  </div>
                </div>

                <div className="p-8 flex-grow flex flex-col">
                  <div className="flex items-center gap-4 text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4">
                    <span className="flex items-center gap-1.5"><Calendar size={14} /> {article.date}</span>
                    <span className="flex items-center gap-1.5"><Clock size={14} /> {article.readTime}</span>
                  </div>
                  <h3 className="text-2xl font-heading font-bold mb-4 group-hover:text-brand-orange transition-colors leading-tight">
                    {article.title}
                  </h3>
                  <p className="text-gray-600 mb-8 leading-relaxed line-clamp-3 font-light">
                    {article.description}
                  </p>
                  <div className="mt-auto pt-6 border-t border-gray-50 flex items-center justify-between text-brand-orange font-bold group-hover:translate-x-1 transition-transform">
                    {article.isExternal ? 'Prečítať v munitnom médiu' : 'Vstúpiť do článku'}
                    <ArrowRight size={20} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 5. DUAL CTA FOOTER */}
      <section className="py-24 bg-brand-dark-blue text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url(/symbol-3.svg)', backgroundSize: '300px', backgroundRepeat: 'repeat' }} />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">

            {/* Book CTA */}
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 p-10 lg:p-14 rounded-[3rem] flex flex-col items-center text-center group hover:bg-white/10 transition-all duration-500">
              <div className="w-20 h-20 bg-brand-orange/20 rounded-2xl flex items-center justify-center text-brand-orange mb-8 group-hover:scale-110 transition-transform">
                <Download size={40} />
              </div>
              <h2 className="text-4xl font-heading font-bold mb-6 leading-tight">
                Získajte celé <br /> <span className="text-brand-orange">know-how v knihe</span>
              </h2>
              <p className="text-xl text-gray-300 mb-10 max-w-md">
                Všetky tieto princípy som spísal do jednej ucelenej mapy k vašej finančnej slobode.
              </p>
              <button
                onClick={() => navigate('/kniha/objednavka')}
                className="w-full bg-brand-orange hover:bg-red-600 text-white font-bold py-5 rounded-2xl shadow-xl transition-all text-xl flex items-center justify-center gap-2"
              >
                <Download size={24} />
                Stiahnuť zdarma
              </button>
            </div>

            {/* Consultation CTA */}
            <div className="bg-white p-10 lg:p-14 rounded-[3rem] flex flex-col items-center text-center group hover:shadow-2xl transition-all duration-500">
              <div className="w-20 h-20 bg-brand-dark-blue/5 rounded-2xl flex items-center justify-center text-brand-dark-blue mb-8 group-hover:scale-110 transition-transform">
                <Users size={40} />
              </div>
              <h2 className="text-4xl font-heading font-bold text-brand-dark-blue mb-6 leading-tight">
                Hľadáte pomoc <br /> <span className="text-gray-400">s implementáciou?</span>
              </h2>
              <p className="text-xl text-gray-600 mb-10 max-w-md text-brand-dark-blue">
                Ak chcete teóriu zmeniť na reálny systém vo vašom živote, som tu pre vás na zoznamovací hovor.
              </p>
              <Link
                to="/konzultace"
                className="w-full border-2 border-brand-dark-blue text-brand-dark-blue hover:bg-brand-dark-blue hover:text-white font-bold py-5 rounded-2xl transition-all text-xl flex items-center justify-center gap-2"
              >
                <Calendar size={24} />
                Bezplatná konzultácia
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;
